import { useState } from 'react';
import type { OptionPageData, AppendixData } from '@core/presentationPrint';

export interface PrintSelections {
  includeCover: boolean;
  includeSummary: boolean;
  selectedOptionIds: string[];
  selectedAppendixIds: string[];
}

interface PrintSelectionUIProps {
  options: OptionPageData[];
  appendices: AppendixData[];
  selections: PrintSelections;
  onChange: (next: PrintSelections) => void;
  onPrint: () => void;
  onBack: () => void;
}

const OPTION_LABELS = ['A', 'B', 'C'];

const OPTION_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  A: { bg: '#eff6ff', border: '#2563eb', text: '#1d4ed8' },
  B: { bg: '#f5f3ff', border: '#7c3aed', text: '#5b21b6' },
  C: { bg: '#f0fdfa', border: '#0d9488', text: '#0f766e' },
};

/**
 * Print selection UI — button-based option picker before export.
 * Screen-only (hidden in print via no-print class).
 */
export function PrintSelectionUI({
  options,
  appendices,
  selections,
  onChange,
  onPrint,
  onBack,
}: PrintSelectionUIProps) {
  const [confirmed, setConfirmed] = useState(false);

  const toggleOption = (id: string) => {
    setConfirmed(false);
    const next = selections.selectedOptionIds.includes(id)
      ? selections.selectedOptionIds.filter((x) => x !== id)
      : [...selections.selectedOptionIds, id];
    onChange({ ...selections, selectedOptionIds: next });
  };

  const toggleAppendix = (id: string) => {
    setConfirmed(false);
    const next = selections.selectedAppendixIds.includes(id)
      ? selections.selectedAppendixIds.filter((x) => x !== id)
      : [...selections.selectedAppendixIds, id];
    onChange({ ...selections, selectedAppendixIds: next });
  };

  const handleConfirm = () => {
    setConfirmed(true);
  };

  const handlePrint = () => {
    setConfirmed(false);
    onPrint();
  };

  return (
    <div className="dp-print-selection no-print">
      {/* Top action bar */}
      <div className="dp-selection-controls" style={{ marginBottom: '1.25rem' }}>
        <button onClick={onBack} className="btn btn-secondary">
          &larr; Back
        </button>

        <button
          onClick={handleConfirm}
          disabled={selections.selectedOptionIds.length === 0}
          style={{
            padding: '0.5rem 1.25rem',
            borderRadius: 6,
            border: '1px solid #d97706',
            background: confirmed ? '#fef3c7' : '#fffbeb',
            color: '#92400e',
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: selections.selectedOptionIds.length === 0 ? 'not-allowed' : 'pointer',
            opacity: selections.selectedOptionIds.length === 0 ? 0.5 : 1,
          }}
        >
          {confirmed ? '✓ Confirmed' : 'Confirm selection'}
        </button>

        <button
          onClick={handlePrint}
          className="btn btn-primary"
          disabled={!confirmed}
          style={{ opacity: confirmed ? 1 : 0.5, cursor: confirmed ? 'pointer' : 'not-allowed' }}
        >
          Print / Save as PDF
        </button>
      </div>

      {!confirmed && selections.selectedOptionIds.length > 0 && (
        <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 1rem' }}>
          Click <strong>Confirm selection</strong> to lock in your choices, then print.
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

        {/* Option selection */}
        <div>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem' }}>
            Options to include
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {options.map((opt, i) => {
              const label = OPTION_LABELS[i] ?? String(i + 1);
              const colors = OPTION_COLORS[label] ?? OPTION_COLORS['A'];
              const isSelected = selections.selectedOptionIds.includes(opt.optionId);

              return (
                <button
                  key={opt.optionId}
                  type="button"
                  onClick={() => toggleOption(opt.optionId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.6rem 0.9rem',
                    borderRadius: 8,
                    border: `2px solid ${isSelected ? colors.border : '#d1d5db'}`,
                    background: isSelected ? colors.bg : '#fff',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                >
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: isSelected ? colors.border : '#e5e7eb',
                    color: isSelected ? '#fff' : '#6b7280',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    flexShrink: 0,
                  }}>
                    {label}
                  </span>
                  <span style={{ flex: 1 }}>
                    <span style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', color: isSelected ? colors.text : '#1e293b' }}>
                      Option {label}
                    </span>
                    <span style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280' }}>
                      {opt.header.systemName}
                    </span>
                  </span>
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: isSelected ? colors.text : '#9ca3af',
                  }}>
                    {isSelected ? 'Selected' : 'Click to add'}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selections.includeCover}
                onChange={() => {
                  setConfirmed(false);
                  onChange({ ...selections, includeCover: !selections.includeCover });
                }}
                style={{ accentColor: '#2563eb', width: 15, height: 15 }}
              />
              Include cover / context page
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selections.includeSummary}
                onChange={() => {
                  setConfirmed(false);
                  onChange({ ...selections, includeSummary: !selections.includeSummary });
                }}
                style={{ accentColor: '#2563eb', width: 15, height: 15 }}
              />
              Include summary page
            </label>
          </div>
        </div>

        {/* Optional upgrades */}
        <div>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem' }}>
            Optional upgrades &amp; reference topics
          </h3>

          {appendices.length === 0 ? (
            <p style={{ fontSize: '0.8rem', color: '#9ca3af' }}>No optional topics available.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {appendices.map((app) => {
                const isSelected = selections.selectedAppendixIds.includes(app.id);
                return (
                  <button
                    key={app.id}
                    type="button"
                    onClick={() => toggleAppendix(app.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      padding: '0.5rem 0.75rem',
                      borderRadius: 6,
                      border: `1.5px solid ${isSelected ? '#22c55e' : '#d1d5db'}`,
                      background: isSelected ? '#f0fdf4' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.82rem',
                    }}
                  >
                    <span style={{
                      display: 'inline-block',
                      width: 16,
                      height: 16,
                      borderRadius: 3,
                      border: `2px solid ${isSelected ? '#16a34a' : '#9ca3af'}`,
                      background: isSelected ? '#16a34a' : 'transparent',
                      flexShrink: 0,
                      position: 'relative',
                    }}>
                      {isSelected && (
                        <svg viewBox="0 0 10 10" style={{ position: 'absolute', inset: 0, fill: 'none', stroke: '#fff', strokeWidth: 1.5 }}>
                          <polyline points="1.5,5 4,8 8.5,2" />
                        </svg>
                      )}
                    </span>
                    <span style={{ color: isSelected ? '#15803d' : '#374151', fontWeight: isSelected ? 600 : 400 }}>
                      {app.title}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {confirmed && (
        <div style={{
          marginTop: '1rem',
          padding: '0.6rem 1rem',
          background: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: 6,
          fontSize: '0.82rem',
          color: '#15803d',
        }}>
          <strong>Selection confirmed.</strong> Options {selections.selectedOptionIds.length > 0 ? selections.selectedOptionIds.join(', ') : 'none'} will be included.
          You can now click <strong>Print / Save as PDF</strong>.
        </div>
      )}
    </div>
  );
}
